import React, { ReactElement } from 'react';
import moment from 'moment';
import ReactDOM from 'react-dom';
import ReactStars from 'react-rating-stars-component';
import Dropdown from 'react-bootstrap/Dropdown';
import DropdownButton from 'react-bootstrap/DropdownButton';
import axios from 'axios';
import { getComputedCSSVariable } from '../../../utils/cssVariableHelpers';
import ReadMore from '../../buttons/read-more';
import {
  FORM_SUBMISSIONS_SELLER_REVIEWS_ADMIN_PREFIX,
  FORM_SUBMISSIONS_SELLER_REVIEWS_BROWSER_API_PREFIX,
  PROFILES_ADMIN_PREFIX,
  USER_ADMIN_PREFIX,
} from '../../../utils/constants';
import UserSVG from '../../search/admin-dashboard-tables/assets/UserSVG';
import useModal from '../../../utils/hooks/useModal';
import ConfirmationModal from '../../search/widgets/ConfirmationModal';
import EditSVG from '../../search/admin-dashboard-tables/assets/EditSVG';

interface Props {
  review: any,
  isAdmin?: boolean,
  canUpdateStatus?: boolean,
}

const SellerReviewCard: React.FC<Props> = ({
  review,
  isAdmin,
  canUpdateStatus,
}): ReactElement => {
  const {
    modalData,
    setModalData,
    showModal,
    setShowModal,
  } = useModal();

  const updateModal = (status: string) => {
    setModalData({
      message: `Would you like to update the status of this review to: ${status}?`,
      onConfirmAsync: () => {
        return new Promise((res, rej) => {
          if (!canUpdateStatus) {
            rej(new Error('You are not permitted to update this review\'s status'));
          }
          axios.patch(
            `${FORM_SUBMISSIONS_SELLER_REVIEWS_BROWSER_API_PREFIX}/${review.id}`,
            { status },
          )
            .then(({ data }) => {
              res(`Status successfully updated to: ${status}`);
            })
            .catch((e) => {
              // console.log(e);
              rej(e);
            });
        });
      },
      onSuccess: () => window.location.reload(),
      onClose: () => {
        setModalData({
          message: '',
          onClose: () => {
          },
        });
      },
    });
  };

  return (
    <div className="seller-review-card py-3 bg-white" key={review.id}>
      <div className="mb-4 px-4 w-100 d-flex align-items-center justify-content-end">
        {
          isAdmin || canUpdateStatus ? (
            <p className="m-0">
              <span className="font-weight-semi-bold">Status: </span>
              <span className="text-capitalize">{review.status}</span>
            </p>
          ) : null
        }
        {canUpdateStatus && (
          <>
            <DropdownButton className="ml-4" id="dropdown-basic-button" title="Update Status">
              <Dropdown.Item
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  updateModal('verified');
                }}
              >
                Verified
              </Dropdown.Item>
              <Dropdown.Item
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  updateModal('pending');
                }}
              >
                Pending
              </Dropdown.Item>
              <Dropdown.Item
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  updateModal('declined');
                }}
              >
                Declined
              </Dropdown.Item>
            </DropdownButton>
            <ConfirmationModal
              show={showModal}
              setShow={setShowModal}
              {...modalData}
            />
          </>
        )}
      </div>
      <div className="row m-0">
        <div className="col-md-4">
          <div className="d-table w-100">
            <div className="d-table-cell w-100 align-top review-user">
              {
                isAdmin
                  ? (
                    <div>
                      <p className="d-flex align-items-center">
                        <span className="font-weight-semi-bold mr-2">Review Of: </span>
                        <a
                          className="m-0 text-dark-btn-txt font-weight-bold d-flex align-items-center"
                          href={`${PROFILES_ADMIN_PREFIX}/${review.seller_id}`}
                        >
                          <span
                            className="d-flex mr-1"
                            style={{
                              width: '16px',
                              height: '16px',
                            }}
                          >
                            <UserSVG />
                          </span>
                          {review.sellerName}
                        </a>
                      </p>
                      <p>
                        <span className="font-weight-semi-bold mr-1">Reviewed By: </span>
                        <a
                          className="m-0 text-dark-btn-txt font-weight-bold"
                          href={`${USER_ADMIN_PREFIX}/${review.author_id}`}
                        >
                          {review.userName}
                        </a>
                      </p>
                      <p>
                        <span className="font-weight-semi-bold">IP Address: </span>
                        <span>{review.ip}</span>
                      </p>
                      <p>
                        <span className="font-weight-semi-bold">Created: </span>
                        <span>{moment.utc(review.created_at).local().format('MM/DD/YYYY h:mm:ss a')}</span>
                      </p>
                      <p>
                        <span className="font-weight-semi-bold">Updated: </span>
                        <span>{moment.utc(review.updated_at).local().format('MM/DD/YYYY h:mm:ss a')}</span>
                      </p>
                    </div>
                  ) : (
                    <p>
                      <strong>
                        {review.userName || review.authorName}
                        .
                      </strong>
                    </p>
                  )
              }
            </div>
          </div>
        </div>
        <div className="col-md-8">
          <div className="row">
            <div className="col-12 d-flex justify-content-between mb-3">
              <ReactStars
                name="rate1"
                starCount={5}
                value={review.rating}
                activeColor={getComputedCSSVariable('yellow')}
                size={19}
                isHalf={true}
                color={getComputedCSSVariable('l-gray')}
                edit={false}
              />
              {!isAdmin && (
                <span>
                  <small>
                    <strong>
                      {moment.utc(review.updated_at).local().format('MM/DD/YYYY')}
                    </strong>
                  </small>
                </span>
              )}
            </div>
          </div>
          <div className="row">
            <div className="col-12 review-txt">
              {
                isAdmin
                  ? (
                    <>
                      <p>{review.message}</p>
                      <div>
                        <a
                          className="edit widescreen btn btn-admin-button-gray"
                          href={`${FORM_SUBMISSIONS_SELLER_REVIEWS_ADMIN_PREFIX}/${review.id}/edit`}
                        >
                          <span className="d-flex align-items-center">
                            <EditSVG />
                            <span className="ml-1">EDIT</span>
                          </span>
                        </a>
                      </div>
                    </>
                  ) : <ReadMore text={review.message} />
              }
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SellerReviewCard;

if (document.getElementsByClassName('react-seller-review-card').length > 0) {
  document.querySelectorAll('.react-seller-review-card').forEach((element) => {
    const review = JSON.parse(element.getAttribute('data-review') ?? '{}');
    const isAdmin: boolean = !!element.getAttribute('data-admin');
    const canUpdateStatus: boolean = !!element.getAttribute('data-canupdatestatus');
    ReactDOM.render(
      <SellerReviewCard
        review={review}
        isAdmin={isAdmin}
        canUpdateStatus={canUpdateStatus}
      />,
      element,
    );
  });
}
